import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RELATION_LABELS } from "@/config/resources";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const relationLabel = (resource, item) => {
  const key = RELATION_LABELS[resource] || "name";
  if (resource === "cases") return `${item.case_number ? item.case_number + " · " : ""}${item[key] || item.title || "Case"}`;
  return item[key] || item.name || item.id;
};

export default function EntityForm({ config, initial, onSubmit, onCancel, submitting }) {
  const { user } = useAuth();
  const [values, setValues] = useState({});
  const [relations, setRelations] = useState({});

  const fields = useMemo(
    () => config.fields.filter((f) => !(f.superOnly && !user?.is_admin)),
    [config, user]
  );

  useEffect(() => {
    const init = {};
    fields.forEach((f) => {
      const v = initial?.[f.key];
      init[f.key] = v !== undefined && v !== null ? v : (f.default !== undefined ? f.default : (f.type === "multiselect" ? [] : ""));
    });
    setValues(init);
  }, [initial, fields]);

  useEffect(() => {
    const resources = [...new Set(fields.filter((f) => f.type === "relation").map((f) => f.resource))];
    resources.forEach(async (res) => {
      try {
        const ep = res === "users" ? "/users" : `/${res}`;
        const { data } = await api.get(ep, { params: { limit: 300 } });
        setRelations((prev) => ({ ...prev, [res]: data.items || [] }));
      } catch (e) {
        setRelations((prev) => ({ ...prev, [res]: [] }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.key]);

  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const toggleMulti = (k, val) => {
    setValues((p) => {
      const arr = Array.isArray(p[k]) ? p[k] : [];
      return { ...p, [k]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
    });
  };

  const submit = (e) => {
    e.preventDefault();
    const payload = {};
    Object.entries(values).forEach(([k, v]) => {
      if (v === "" || v === null) return;
      payload[k] = v;
    });
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="entity-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3.5">
        {fields.map((f) => (
          <div key={f.key} className={f.span === 2 ? "sm:col-span-2" : ""}>
            <Label className="text-[13px] font-medium text-[#252221] mb-1.5 block">
              {f.label} {f.required && <span className="text-[#9C1E22]">*</span>}
            </Label>
            {(f.type === "text" || f.type === "email" || f.type === "tel" || f.type === "number") && (
              <Input
                type={f.type === "number" ? "number" : f.type}
                value={values[f.key] ?? ""}
                onChange={(e) => set(f.key, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                required={f.required}
                data-testid={`field-${f.key}`}
                className="h-10"
              />
            )}
            {(f.type === "date" || f.type === "datetime-local") && (
              <Input
                type={f.type}
                value={values[f.key] ? String(values[f.key]).slice(0, f.type === "date" ? 10 : 16) : ""}
                onChange={(e) => set(f.key, e.target.value)}
                required={f.required}
                data-testid={`field-${f.key}`}
                className="h-10"
              />
            )}
            {f.type === "textarea" && (
              <Textarea
                value={values[f.key] ?? ""}
                onChange={(e) => set(f.key, e.target.value)}
                rows={3}
                data-testid={`field-${f.key}`}
              />
            )}
            {f.type === "select" && (
              <Select value={values[f.key] || ""} onValueChange={(v) => set(f.key, v)}>
                <SelectTrigger data-testid={`field-${f.key}`} className="h-10">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  {f.options.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {f.type === "relation" && (
              <Select value={values[f.key] || ""} onValueChange={(v) => set(f.key, v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid={`field-${f.key}`} className="h-10">
                  <SelectValue placeholder="Select..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {(relations[f.resource] || []).map((item) => (
                    <SelectItem key={item.id} value={item.id}>{relationLabel(f.resource, item)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {f.type === "multiselect" && (
              <div className="flex flex-wrap gap-2 pt-1">
                {f.options.map((o) => {
                  const active = Array.isArray(values[f.key]) && values[f.key].includes(o.value);
                  return (
                    <button
                      type="button"
                      key={o.value}
                      onClick={() => toggleMulti(f.key, o.value)}
                      data-testid={`field-${f.key}-${o.value}`}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        active ? "text-white border-transparent" : "bg-white text-[#1C4358] border-[#E6EDF2] hover:bg-[rgba(155,185,206,0.12)]"
                      }`}
                      style={active ? { backgroundColor: "var(--mmm-navy)" } : {}}
                    >
                      {o.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="form-cancel-button">Cancel</Button>
        <Button type="submit" disabled={submitting} data-testid="form-submit-button"
          className="text-white" style={{ backgroundColor: "var(--mmm-red)" }}>
          {submitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
}
